import { useEffect, useState } from 'react';
import { resolveApiMediaUrl } from '../../api/client.js';

function Chevron() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * @param {unknown} raw
 */
function resolveSlideImage(raw) {
  if (typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s) || s.startsWith('/')) return s;
  return resolveApiMediaUrl(s);
}

/**
 * Carrossel hero estilo Magalu/Havan (só imagem).
 *
 * @param {{
 *   slides: Array<{
 *     image_url: string,
 *     link_type?: string,
 *     secao?: string,
 *     product_id?: number|null,
 *     href?: string,
 *   }>,
 *   autoplaySeconds?: number,
 *   onNavigateToSection?: (secao: string) => void,
 *   onOpenProduct?: (id: number) => void,
 *   className?: string,
 * }} props
 */
export function StoreHeroCarousel({
  slides,
  autoplaySeconds = 5,
  onNavigateToSection,
  onOpenProduct,
  className = '',
}) {
  const list = Array.isArray(slides) ? slides.filter((s) => s && s.image_url) : [];
  const len = list.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (len <= 0) setIdx(0);
    else setIdx((i) => i % len);
  }, [len]);

  useEffect(() => {
    if (len <= 1 || paused) return undefined;
    const ms = Math.max(3, Math.min(10, Number(autoplaySeconds) || 5)) * 1000;
    const t = window.setInterval(() => {
      setIdx((i) => (i + 1) % len);
    }, ms);
    return () => window.clearInterval(t);
  }, [len, paused, autoplaySeconds]);

  if (len <= 0) return null;

  const go = (d) => {
    setIdx((i) => (i + d + len) % len);
  };

  function activateSlide(slide) {
    const type = String(slide?.link_type || 'none');
    if (type === 'secao') {
      const secao = String(slide?.secao || '').trim();
      if (secao && onNavigateToSection) onNavigateToSection(secao);
      return;
    }
    if (type === 'product') {
      const id = Number(slide?.product_id);
      if (Number.isFinite(id) && id >= 1 && onOpenProduct) onOpenProduct(Math.trunc(id));
      return;
    }
    if (type === 'url') {
      const href = typeof slide?.href === 'string' ? slide.href.trim() : '';
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
    }
  }

  function slideIsClickable(slide) {
    const type = String(slide?.link_type || 'none');
    if (type === 'secao') return Boolean(String(slide?.secao || '').trim());
    if (type === 'product') {
      const id = Number(slide?.product_id);
      return Number.isFinite(id) && id >= 1;
    }
    if (type === 'url') return Boolean(typeof slide?.href === 'string' && slide.href.trim());
    return false;
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-zinc-200/80 bg-zinc-100 shadow-sm dark:border-zinc-700/80 dark:bg-zinc-900 ${className}`.trim()}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative aspect-[21/9] min-h-[140px] w-full max-sm:aspect-[16/10]">
        {list.map((slide, i) => {
          const active = i === idx;
          const img = resolveSlideImage(slide.image_url);
          const clickable = active && slideIsClickable(slide);
          return (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-500 ${active ? 'z-[1] opacity-100' : 'pointer-events-none z-0 opacity-0'}`}
            >
              {img ? (
                <img
                  alt=""
                  src={img}
                  className="h-full w-full object-cover"
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              ) : (
                <div className="h-full w-full bg-zinc-200 dark:bg-zinc-800" />
              )}
              {clickable ? (
                <button
                  type="button"
                  className="absolute inset-0 z-[2] cursor-pointer bg-transparent"
                  aria-label="Abrir destaque"
                  onClick={() => activateSlide(slide)}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      {len > 1 ? (
        <>
          <button
            type="button"
            className="absolute left-2 top-1/2 z-[3] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/90 text-zinc-800 shadow-sm backdrop-blur-sm transition hover:bg-white dark:bg-zinc-900/80 dark:text-zinc-100"
            aria-label="Slide anterior"
            onClick={() => go(-1)}
          >
            <span className="inline-block rotate-180">
              <Chevron />
            </span>
          </button>
          <button
            type="button"
            className="absolute right-2 top-1/2 z-[3] flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/90 text-zinc-800 shadow-sm backdrop-blur-sm transition hover:bg-white dark:bg-zinc-900/80 dark:text-zinc-100"
            aria-label="Próximo slide"
            onClick={() => go(1)}
          >
            <Chevron />
          </button>
          <div className="absolute bottom-2.5 left-0 right-0 z-[3] flex justify-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === idx ? 'w-6 bg-[var(--st-600)]' : 'w-2 bg-white/80 ring-1 ring-black/10'}`}
                onClick={() => setIdx(i)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
